// ============================================================
// GUEST CART UTILITIES
// Enables seamless shopping & checkout without login
// ============================================================

const GUEST_CART_KEY = "vanya_guest_cart";

export const getGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Failed to parse guest cart:", e);
    return [];
  }
};

export const saveGuestCart = (items) => {
  try {
    const safeItems = Array.isArray(items) ? items : [];
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(safeItems));
    // Dispatch events so Navbar, Cart drawer, etc. automatically refresh
    window.dispatchEvent(new Event("vanya_cart_change"));
    window.dispatchEvent(new Event("storage"));
  } catch (e) {
    console.error("Failed to save guest cart:", e);
  }
};

// ============================================================
// GET PRODUCT STOCK (Accurate across variants, sizes & single items)
// ============================================================
export const getProductStock = (product, variant = null, colour = null, size = null) => {
  if (!product) return 0;
  if (product.is_hold) return 0;

  // 1. If specific size is requested
  if (size && variant && Array.isArray(variant.sizes)) {
    const foundSize = variant.sizes.find(
      (s) => String(s.size || "").toLowerCase().trim() === String(size).toLowerCase().trim()
    );
    if (foundSize) {
      return Number(foundSize.stock ?? 0);
    }
  }

  // 2. If specific variant is provided
  if (variant) {
    if (Array.isArray(variant.sizes) && variant.sizes.length > 0) {
      if (size) {
        const foundSize = variant.sizes.find(
          (s) => String(s.size || "").toLowerCase().trim() === String(size).toLowerCase().trim()
        );
        return Number(foundSize?.stock ?? 0);
      }
      return variant.sizes.reduce((acc, s) => acc + Number(s.stock ?? 0), 0);
    }
    if (variant.stock !== undefined && variant.stock !== null) {
      return Number(variant.stock ?? 0);
    }
  }

  // 3. If colour is provided, locate variant
  if (colour && Array.isArray(product.variants) && product.variants.length > 0) {
    const matched = product.variants.find(
      (v) => String(v.colour || v.color || "").toLowerCase().trim() === String(colour).toLowerCase().trim()
    );
    if (matched) {
      return getProductStock(product, matched, colour, size);
    }
  }

  // 4. If product has variants, total stock is sum across all variants
  if (Array.isArray(product.variants) && product.variants.length > 0) {
    let total = 0;
    for (const v of product.variants) {
      if (Array.isArray(v.sizes) && v.sizes.length > 0) {
        total += v.sizes.reduce((acc, s) => acc + Number(s.stock ?? 0), 0);
      } else {
        total += Number(v.stock ?? 0);
      }
    }
    return total;
  }

  // 5. Fallback to product.stock if no variants exist
  return Number(product.stock ?? 0);
};

// ============================================================
// RESOLVE VARIANT, COLOUR & SIZE DEFAULTS FOR ANY PRODUCT
// Ensures Dress Materials (size-based variants) & Sarees work
// seamlessly from Home, Shop, NewArrivals, BestSellers & Detail page
// ============================================================
export const resolveCartVariantDetails = (product, options = {}) => {
  if (!product) {
    return {
      chosenColour: null,
      chosenSize: null,
      matchedVariant: null,
      matchedSizeObj: null,
      cartVariant: null,
      price: 0,
      oldPrice: 0,
      discount: 0,
      stock: 0,
      imgUrl: "",
    };
  }

  const colours = getProductColours(product);
  const variants = Array.isArray(product.variants) ? product.variants.filter(Boolean) : [];

  let chosenColour =
    options.colour ||
    options.variant?.colour ||
    options.variant?.color ||
    (colours.length === 1 ? colours[0] : null);

  let matchedVariant = options.variant || null;

  // Locate full variant from product.variants if needed
  if (variants.length > 0) {
    if (chosenColour) {
      const foundByColour = variants.find(
        (v) =>
          String(v.colour || v.color || "").trim().toLowerCase() ===
          String(chosenColour).trim().toLowerCase()
      );
      if (foundByColour) {
        matchedVariant = foundByColour;
      }
    }
    if (!matchedVariant) {
      // Pick first variant with stock > 0, or fallback to variants[0]
      matchedVariant =
        variants.find((v) => getProductStock(product, v, v.colour || v.color, null) > 0) ||
        variants[0];
    }
    if (!chosenColour && matchedVariant) {
      chosenColour = matchedVariant.colour || matchedVariant.color || null;
    }
  }

  if (!chosenColour) {
    chosenColour = product.colour || product.color || null;
  }

  // Locate size object if variant has sizes array (e.g. Dress Materials)
  let matchedSizeObj = null;
  let chosenSize = options.size || options.variant?.size || null;

  if (matchedVariant && Array.isArray(matchedVariant.sizes) && matchedVariant.sizes.length > 0) {
    if (chosenSize) {
      matchedSizeObj =
        matchedVariant.sizes.find(
          (s) =>
            String(s.size || "").trim().toLowerCase() ===
            String(chosenSize).trim().toLowerCase()
        ) || null;
    }
    if (!matchedSizeObj) {
      // Auto-select first size with stock > 0, or first size in list
      matchedSizeObj =
        matchedVariant.sizes.find((s) => Number(s.stock ?? 0) > 0) ||
        matchedVariant.sizes[0] ||
        null;
      if (matchedSizeObj?.size) {
        chosenSize = matchedSizeObj.size;
      }
    }
  }

  if (!chosenSize) {
    chosenSize =
      (Array.isArray(product.sizes) && product.sizes.length > 0
        ? typeof product.sizes[0] === "object"
          ? product.sizes[0].size
          : product.sizes[0]
        : product.size) || null;
  }

  const price = Number(
    matchedSizeObj?.price ??
      options.variant?.price ??
      matchedVariant?.price ??
      product.price ??
      0
  );

  const oldPrice = Number(
    matchedSizeObj?.oldPrice ??
      matchedSizeObj?.old_price ??
      options.variant?.oldPrice ??
      options.variant?.old_price ??
      matchedVariant?.oldPrice ??
      matchedVariant?.old_price ??
      product.oldPrice ??
      product.old_price ??
      0
  );

  const discount = Number(
    matchedSizeObj?.discount ??
      options.variant?.discount ??
      matchedVariant?.discount ??
      (typeof product.discount === "string"
        ? parseFloat(product.discount.replace(/[^0-9.]/g, ""))
        : product.discount) ??
      0
  );

  const stock = getProductStock(product, matchedVariant, chosenColour, chosenSize);

  const imgUrl =
    matchedVariant?.mainImage ||
    matchedVariant?.main_image ||
    matchedVariant?.img_url ||
    matchedVariant?.image ||
    matchedVariant?.imageUrl ||
    (Array.isArray(matchedVariant?.thumbnails) && matchedVariant.thumbnails[0]) ||
    options.variant?.mainImage ||
    options.variant?.img_url ||
    product.img_url ||
    product.image ||
    product.img ||
    product.imageUrl ||
    product.mainImage ||
    (Array.isArray(product.thumbnails) && product.thumbnails[0]) ||
    (Array.isArray(product.images) && product.images[0]) ||
    "";

  const cartVariant = {
    ...(matchedVariant || {}),
    id: matchedVariant?.id ?? matchedVariant?.variant_id ?? null,
    variant_id: matchedVariant?.id ?? matchedVariant?.variant_id ?? null,
    colour: chosenColour,
    size: chosenSize,
    price,
    oldPrice,
    old_price: oldPrice,
    discount,
    stock,
    img_url: imgUrl,
    mainImage: imgUrl,
    thumbnails: Array.isArray(matchedVariant?.thumbnails)
      ? matchedVariant.thumbnails
      : Array.isArray(product.thumbnails)
      ? product.thumbnails
      : imgUrl
      ? [imgUrl]
      : [],
    sizes: Array.isArray(matchedVariant?.sizes) ? matchedVariant.sizes : [],
  };

  return {
    chosenColour,
    chosenSize,
    matchedVariant,
    matchedSizeObj,
    cartVariant,
    price,
    oldPrice,
    discount,
    stock,
    imgUrl,
  };
};

// ============================================================
// PROTECT PRODUCT STOCK AGAINST LEGACY BACKEND CART DEDUCTION
// Ensures adding/removing from cart never reduces vanayaproducts stock
// ============================================================
export const restoreProductStockIfDeductedByBackend = async (product) => {
  if (!product || !product.id) return;
  const originalVariants = Array.isArray(product.variants) ? product.variants : [];
  const originalTotalStock = getProductStock(product);
  if (originalTotalStock <= 0) return;

  try {
    const res = await fetch("https://api.vanyacollectionsrajahmundry.com/products/all");
    if (!res.ok) return;
    const allProducts = await res.json();
    const list = Array.isArray(allProducts) ? allProducts : allProducts.products || [];
    const dbProduct = list.find((p) => Number(p.id) === Number(product.id));
    if (!dbProduct) return;

    const currentDbStock = getProductStock(dbProduct);
    if (currentDbStock < originalTotalStock) {
      const fd = new FormData();
      fd.append("name", dbProduct.name || product.name || "");
      fd.append("category", dbProduct.category || product.category || "");
      fd.append("sub_category", dbProduct.sub_category || product.sub_category || "");
      fd.append("price", String(Number(dbProduct.price ?? product.price ?? 0)));
      fd.append("old_price", String(Number(dbProduct.old_price ?? product.old_price ?? product.oldPrice ?? 0)));
      fd.append("discount", String(Number(dbProduct.discount ?? product.discount ?? 0)));
      fd.append("stock", String(Number(product.stock || originalTotalStock)));
      fd.append("type", dbProduct.type || product.type || "New Arrival");
      fd.append("fabric", dbProduct.fabric || product.fabric || "");
      fd.append("length", dbProduct.length || product.length || "");
      fd.append("occasion", dbProduct.occasion || product.occasion || "");
      fd.append("authenticity", dbProduct.authenticity || product.authenticity || "");
      fd.append("is_hold", String(Boolean(dbProduct.is_hold)));
      fd.append("existing_img_url", dbProduct.img_url || product.img_url || "");
      fd.append(
        "existing_thumbnails",
        JSON.stringify(Array.isArray(dbProduct.thumbnails) ? dbProduct.thumbnails : [])
      );
      fd.append("variants", JSON.stringify(originalVariants));

      await fetch(`https://api.vanyacollectionsrajahmundry.com/products/update/${product.id}`, {
        method: "PUT",
        body: fd,
      });
    }
  } catch (err) {
    console.warn("Stock preservation check skipped:", err);
  }
};

export const addGuestCartItem = (product, quantity = 1, variant = null, colour = null, size = null) => {
  const currentItems = getGuestCart();
  const productId = Number(product.id || product.product_id);

  if (product.is_hold) {
    alert("Sorry, this item is currently unavailable.");
    return currentItems;
  }

  const resolved = resolveCartVariantDetails(product, { variant, colour, size });
  const selectedColour = resolved.chosenColour;
  const selectedSize = resolved.chosenSize;
  const availableStock = resolved.stock;

  if (availableStock <= 0) {
    alert("Sorry, this item is currently out of stock.");
    return currentItems;
  }

  // Check if identical item (same product ID, colour, size) already exists
  const existingIndex = currentItems.findIndex(
    (item) =>
      Number(item.product_id || item.id) === productId &&
      String(item.colour || item.variant?.colour || "").trim().toLowerCase() ===
        String(selectedColour || "").trim().toLowerCase() &&
      String(item.size || item.variant?.size || "").trim().toLowerCase() ===
        String(selectedSize || "").trim().toLowerCase()
  );

  const price = resolved.price;
  const oldPrice = resolved.oldPrice;
  const discount = resolved.discount;
  const stock = availableStock;
  const imgUrl = resolved.imgUrl;

  if (existingIndex > -1) {
    const existing = currentItems[existingIndex];
    const currentQty = Number(existing.quantity || 1);
    if (currentQty >= stock) {
      alert(`Only ${stock} piece(s) available for this item.`);
      return currentItems;
    }
    const newQty = Math.min(currentQty + Number(quantity || 1), stock);
    currentItems[existingIndex] = {
      ...existing,
      price,
      oldPrice,
      discount,
      img_url: existing.img_url || imgUrl,
      image: existing.image || imgUrl,
      imageUrl: existing.imageUrl || imgUrl,
      img: existing.img || imgUrl,
      mainImage: existing.mainImage || imgUrl,
      colour: selectedColour,
      size: selectedSize,
      variant: resolved.cartVariant,
      quantity: newQty,
      subtotal: price * newQty,
      stock,
    };
  } else {
    const safeQty = Math.min(Number(quantity || 1), stock);
    const guestCartId = `guest_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    const newItem = {
      id: guestCartId,
      product_id: productId,
      name: product.name || product.title || "Saree",
      price: price,
      oldPrice: oldPrice,
      discount: discount,
      img_url: imgUrl,
      image: imgUrl,
      imageUrl: imgUrl,
      img: imgUrl,
      mainImage: imgUrl,
      colour: selectedColour,
      size: selectedSize,
      variant: resolved.cartVariant,
      quantity: safeQty,
      stock: stock,
      category: product.category || product.cat || "",
      sub_category: product.sub_category || "",
      subtotal: price * safeQty,
      isGuest: true,
    };
    currentItems.push(newItem);
  }

  saveGuestCart(currentItems);
  return currentItems;
};

export const updateGuestCartQty = (cartId, newQty) => {
  let items = getGuestCart();
  if (newQty <= 0) {
    items = items.filter((item) => item.id !== cartId);
  } else {
    items = items.map((item) => {
      if (item.id === cartId) {
        const price = Number(item.price || 0);
        const itemStock = Number(item.stock ?? 99);
        const safeQty = itemStock > 0 ? Math.min(newQty, itemStock) : newQty;
        return {
          ...item,
          quantity: safeQty,
          subtotal: price * safeQty,
        };
      }
      return item;
    });
  }
  saveGuestCart(items);
  return items;
};

export const removeGuestCartItem = (cartId) => {
  const items = getGuestCart().filter((item) => item.id !== cartId);
  saveGuestCart(items);
  return items;
};

export const clearGuestCart = () => {
  saveGuestCart([]);
};

// ============================================================
// GET UNIQUE PRODUCT COLOURS
// ============================================================
export const getProductColours = (product) => {
  if (!product) return [];
  const colourSet = new Set();

  if (Array.isArray(product.variants) && product.variants.length > 0) {
    product.variants.forEach((v) => {
      const col = v?.colour || v?.color;
      if (col && typeof col === "string" && col.trim()) {
        colourSet.add(col.trim());
      }
    });
  }

  if (colourSet.size === 0) {
    const rawColour = product.colour || product.color;
    if (typeof rawColour === "string" && rawColour.trim()) {
      if (rawColour.includes(",")) {
        rawColour.split(",").forEach((c) => {
          if (c.trim()) colourSet.add(c.trim());
        });
      } else {
        colourSet.add(rawColour.trim());
      }
    } else if (Array.isArray(product.colors || product.colours)) {
      (product.colors || product.colours).forEach((c) => {
        if (typeof c === "string" && c.trim()) colourSet.add(c.trim());
      });
    }
  }

  return Array.from(colourSet);
};

// ============================================================
// LOGGED-IN & UNIFIED CART CACHE
// Synchronizes cart state across all product cards & pages
// ============================================================

export const USER_CART_CACHE_KEY = "vanya_user_cart_cache";

export const getLoggedInUser = () => {
  try {
    const raw = localStorage.getItem("user") || localStorage.getItem("customer");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const getUserCartCache = () => {
  try {
    const raw = localStorage.getItem(USER_CART_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const saveUserCartCache = (items) => {
  try {
    const safeItems = Array.isArray(items) ? items : [];
    localStorage.setItem(USER_CART_CACHE_KEY, JSON.stringify(safeItems));
    window.dispatchEvent(new Event("vanya_cart_change"));
    window.dispatchEvent(new Event("storage"));
  } catch (e) {
    console.error("Failed to save user cart cache:", e);
  }
};

export const syncUserCartCache = async (userId) => {
  if (!userId) return [];
  try {
    const res = await fetch(`https://api.vanyacollectionsrajahmundry.com/cart/${userId}`);
    if (!res.ok) throw new Error("Failed to fetch user cart");
    const data = await res.json();
    const items = Array.isArray(data) ? data : (data.items || []);
    saveUserCartCache(items);
    return items;
  } catch (err) {
    console.warn("syncUserCartCache failed:", err);
    return getUserCartCache();
  }
};

export const getUnifiedCart = () => {
  const user = getLoggedInUser();
  if (user) {
    return getUserCartCache();
  }
  return getGuestCart();
};

// ============================================================
// GET PRODUCT CART QUANTITY
// Returns current quantity of this product/variant in bag
// ============================================================
export const getProductCartQty = (productId, colour = null, size = null) => {
  if (!productId) return 0;
  const items = getUnifiedCart();
  const pid = Number(productId);

  const matchingItems = items.filter((item) => {
    const itemPid = Number(item.product_id || item.id);
    if (itemPid !== pid) return false;

    if (colour) {
      const itemColour = String(item.colour || item.variant?.colour || "").trim().toLowerCase();
      const targetColour = String(colour).trim().toLowerCase();
      if (itemColour && itemColour !== targetColour) return false;
    }

    if (size) {
      const itemSize = String(item.size || item.variant?.size || "").trim().toLowerCase();
      const targetSize = String(size).trim().toLowerCase();
      if (itemSize && itemSize !== targetSize) return false;
    }

    return true;
  });

  return matchingItems.reduce((acc, item) => acc + Number(item.quantity || 1), 0);
};

export const getProductCartItem = (productId, colour = null, size = null) => {
  if (!productId) return null;
  const items = getUnifiedCart();
  const pid = Number(productId);

  return items.find((item) => {
    const itemPid = Number(item.product_id || item.id);
    if (itemPid !== pid) return false;

    if (colour) {
      const itemColour = String(item.colour || item.variant?.colour || "").trim().toLowerCase();
      const targetColour = String(colour).trim().toLowerCase();
      if (itemColour && itemColour !== targetColour) return false;
    }

    if (size) {
      const itemSize = String(item.size || item.variant?.size || "").trim().toLowerCase();
      const targetSize = String(size).trim().toLowerCase();
      if (itemSize && itemSize !== targetSize) return false;
    }

    return true;
  }) || null;
};

// ============================================================
// UNIVERSAL INCREMENT CART QUANTITY
// ============================================================
export const incrementCartQty = async (product, options = {}) => {
  if (!product) return { success: false, message: "Product missing" };

  const productId = Number(product.id || product.product_id);
  const resolved = resolveCartVariantDetails(product, options);
  const currentQty = getProductCartQty(productId, options.colour || null, options.size || null);
  const availableStock = resolved.stock || getProductStock(product, options.variant, options.colour, options.size);

  if (availableStock <= 0) {
    return { success: false, outOfStock: true, message: "Product is out of stock" };
  }

  if (currentQty >= availableStock) {
    return {
      success: false,
      maxReached: true,
      message: `Only ${availableStock} item(s) available in stock.`,
    };
  }

  // If item is not in cart yet, add it
  if (currentQty === 0) {
    const addResult = await handleUniversalAddToCart(product, {
      ...options,
      colour: resolved.chosenColour,
      size: resolved.chosenSize,
      variant: resolved.cartVariant,
      quantity: 1,
    });
    return {
      ...addResult,
      newQty: addResult.success ? 1 : 0,
    };
  }

  const user = getLoggedInUser();
  const newQty = currentQty + 1;
  const cartItem =
    getProductCartItem(productId, options.colour, options.size) ||
    getProductCartItem(productId, resolved.chosenColour, resolved.chosenSize) ||
    getProductCartItem(productId);

  if (!user) {
    // Guest cart
    if (cartItem) {
      updateGuestCartQty(cartItem.id, newQty);
    } else {
      addGuestCartItem(product, 1, resolved.cartVariant, resolved.chosenColour, resolved.chosenSize);
    }
    return { success: true, newQty };
  }

  // Logged in user
  if (cartItem && cartItem.id && !String(cartItem.id).startsWith("temp_")) {
    // Optimistically update cache
    const cached = getUserCartCache();
    const updatedCache = cached.map((item) =>
      item.id === cartItem.id
        ? { ...item, quantity: newQty, subtotal: Number(item.price || resolved.price || 0) * newQty }
        : item
    );
    saveUserCartCache(updatedCache);

    try {
      await fetch(`https://api.vanyacollectionsrajahmundry.com/cart/update/${cartItem.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          cart_id: cartItem.id,
          product_id: productId,
          colour: cartItem.colour || cartItem.variant?.colour || resolved.chosenColour,
          size: cartItem.size || cartItem.variant?.size || resolved.chosenSize,
          quantity: newQty,
        }),
      });
      await syncUserCartCache(user.id);
    } catch (err) {
      console.warn("Failed to update cart on backend:", err);
    }
  } else {
    await handleUniversalAddToCart(product, {
      ...options,
      colour: resolved.chosenColour,
      size: resolved.chosenSize,
      variant: resolved.cartVariant,
      quantity: 1,
    });
  }

  return { success: true, newQty };
};

// ============================================================
// UNIVERSAL DECREMENT CART QUANTITY
// ============================================================
export const decrementCartQty = async (product, options = {}) => {
  if (!product) return { success: false, message: "Product missing" };

  const productId = Number(product.id || product.product_id);
  const resolved = resolveCartVariantDetails(product, options);
  const currentQty = getProductCartQty(productId, options.colour || null, options.size || null);

  if (currentQty <= 0) {
    return { success: false, newQty: 0 };
  }

  const user = getLoggedInUser();
  const cartItem =
    getProductCartItem(productId, options.colour, options.size) ||
    getProductCartItem(productId, resolved.chosenColour, resolved.chosenSize) ||
    getProductCartItem(productId);

  if (!cartItem) return { success: false, newQty: 0 };

  const itemQty = Number(cartItem.quantity || currentQty || 1);
  const newQty = itemQty - 1;

  if (newQty <= 0) {
    // Remove item completely
    if (!user) {
      removeGuestCartItem(cartItem.id);
    } else {
      const cached = getUserCartCache().filter((item) => item.id !== cartItem.id);
      saveUserCartCache(cached);
      try {
        if (!String(cartItem.id).startsWith("temp_")) {
          await fetch(`https://api.vanyacollectionsrajahmundry.com/cart/delete/${cartItem.id}`, {
            method: "DELETE",
          });
        }
        await syncUserCartCache(user.id);
      } catch (err) {
        console.warn("Failed to delete cart item on backend:", err);
      }
    }
    return { success: true, newQty: 0, removed: true };
  }

  // Update to newQty (>= 1)
  if (!user) {
    updateGuestCartQty(cartItem.id, newQty);
  } else {
    const cached = getUserCartCache().map((item) =>
      item.id === cartItem.id
        ? { ...item, quantity: newQty, subtotal: Number(item.price || resolved.price || 0) * newQty }
        : item
    );
    saveUserCartCache(cached);
    try {
      if (!String(cartItem.id).startsWith("temp_")) {
        await fetch(`https://api.vanyacollectionsrajahmundry.com/cart/update/${cartItem.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: user.id,
            cart_id: cartItem.id,
            product_id: productId,
            colour: cartItem.colour || cartItem.variant?.colour || resolved.chosenColour,
            size: cartItem.size || cartItem.variant?.size || resolved.chosenSize,
            quantity: newQty,
          }),
        });
      }
      await syncUserCartCache(user.id);
    } catch (err) {
      console.warn("Failed to update cart item on backend:", err);
    }
  }

  return { success: true, newQty };
};

// ============================================================
// UNIVERSAL ADD TO CART
// Handles guest vs logged-in user & single colour vs multi colour with strict stock validation
// ============================================================
export const handleUniversalAddToCart = async (product, options = {}) => {
  if (!product) {
    return { success: false, message: "Product details unavailable." };
  }

  if (product.is_hold) {
    return {
      success: false,
      outOfStock: true,
      message: `"${product.name || 'Product'}" is currently unavailable.`,
    };
  }

  // 1. Overall product stock check
  const totalStock = getProductStock(product);
  if (totalStock <= 0) {
    return {
      success: false,
      outOfStock: true,
      message: `"${product.name || 'Product'}" is currently out of stock.`,
    };
  }

  const colours = getProductColours(product);

  // If product has multiple colours and user hasn't selected one yet
  if (colours.length > 1 && !options.colour && !options.variant?.colour) {
    return {
      needsColourSelection: true,
      message: "Please select a colour first.",
      colours,
    };
  }

  const resolved = resolveCartVariantDetails(product, options);
  const {
    chosenColour,
    chosenSize,
    cartVariant,
    price,
    oldPrice,
    discount,
    stock: specificStock,
    imgUrl,
  } = resolved;

  const quantity = Number(options.quantity || 1);
  const productId = Number(product.id || product.product_id);

  // 2. Specific variant stock check
  if (specificStock <= 0) {
    return {
      success: false,
      outOfStock: true,
      message: `Selected variant (${chosenColour || ""} ${chosenSize || ""}) is currently out of stock.`,
    };
  }

  // 3. Check against existing quantity in bag
  const currentInBag = getProductCartQty(productId, chosenColour, chosenSize);
  if (currentInBag + quantity > specificStock) {
    return {
      success: false,
      maxReached: true,
      message: `Only ${specificStock} item(s) available in stock (${currentInBag} already in your bag).`,
    };
  }

  // Check login
  const user = getLoggedInUser();
  const userId = user?.id || user?.user_id;

  if (userId) {
    // Snapshot previous cache and optimistically update user cart cache immediately
    const previousCache = getUserCartCache();
    const existingIdx = previousCache.findIndex(
      (item) =>
        Number(item.product_id || item.id) === productId &&
        String(item.colour || item.variant?.colour || "").trim().toLowerCase() ===
          String(chosenColour || "").trim().toLowerCase() &&
        String(item.size || item.variant?.size || "").trim().toLowerCase() ===
          String(chosenSize || "").trim().toLowerCase()
    );

    let optimisticCache;
    const tempId = `temp_${Date.now()}_${productId}`;
    if (existingIdx > -1) {
      optimisticCache = previousCache.map((item, idx) => {
        if (idx !== existingIdx) return item;
        const nextQ = Math.min(specificStock, Number(item.quantity || 1) + quantity);
        return {
          ...item,
          quantity: nextQ,
          subtotal: Number(item.price || price || 0) * nextQ,
        };
      });
    } else {
      const optimisticItem = {
        id: tempId,
        user_id: userId,
        product_id: productId,
        name: product.name || product.title || "Product",
        category: product.category || product.cat || "",
        sub_category: product.sub_category || "",
        price,
        oldPrice,
        old_price: oldPrice,
        discount,
        stock: specificStock,
        img_url: imgUrl,
        image: imgUrl,
        mainImage: imgUrl,
        colour: chosenColour,
        size: chosenSize,
        variant: cartVariant,
        quantity,
        subtotal: price * quantity,
      };
      optimisticCache = [optimisticItem, ...previousCache];
    }
    saveUserCartCache(optimisticCache);

    try {
      const response = await fetch("https://api.vanyacollectionsrajahmundry.com/cart/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          product_id: productId,
          quantity,
          colour: chosenColour,
          size: chosenSize,
          variant: cartVariant,
          selected_variant: cartVariant,
          selected_colour: chosenColour,
          selected_size: chosenSize,
          variant_price: price,
          variant_old_price: oldPrice,
          variant_discount: discount,
          variant_stock: specificStock,
          variant_image: imgUrl,
          variant_main_image: imgUrl,
        }),
      });

      const resData = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Rollback optimistic cache on backend rejection
        saveUserCartCache(previousCache);
        return {
          success: false,
          message: resData.error || "Failed to add to cart",
        };
      }

      // Replace tempId with real database id immediately
      if (resData?.item?.id) {
        const currentCache = getUserCartCache();
        const withRealId = currentCache.map((it) =>
          it.id === tempId ? { ...it, id: resData.item.id } : it
        );
        saveUserCartCache(withRealId);
      }

      // Protect vanayaproducts stock if remote backend is still running legacy cart.js
      await restoreProductStockIfDeductedByBackend(product);

      // Sync user cart cache with server
      await syncUserCartCache(userId);

      window.dispatchEvent(new Event("vanya_cart_change"));
      window.dispatchEvent(new Event("storage"));

      return {
        success: true,
        message: `${product.name || "Product"} added to your bag ✨`,
      };
    } catch (err) {
      console.error("Add to cart error:", err);
      saveUserCartCache(previousCache);
      return {
        success: false,
        message: err.message || "Failed to add to cart",
      };
    }
  } else {
    // Guest user -> directly save to guest cart
    addGuestCartItem(product, quantity, cartVariant, chosenColour, chosenSize);
    return {
      success: true,
      message: `${product.name || "Product"} added to your bag ✨`,
    };
  }
};

